import axios, { AxiosError, AxiosInstance } from 'axios';
import { installAuthInterceptors } from '@/lib/auth/axios-auth';
import type {
  PreInspectionCreateRequest,
  PreInspectionCreateResponse,
  PreInspectionDetailResponse,
  PreInspectionListResponse,
  PreInspectionUpdateRequest,
} from '@/types/pre-inspection';
import type { PaidPreInspectionListResponse } from '@/types/paid-pre-inspection';

const BASE_PATH = '/api/v1/insurance/pre-inspections';
const PAID_PATH = `${BASE_PATH}/paid`;
const TENANT_STATUS_PATH = '/api/v1/admin/status';
const JOBS_PATH = '/api/v1/jobs';
const PARTNERS_PATH = '/api/v1/partners/';
const AI_QC_PATH = '/api/v1/ai/pre-inspection-qc';
const REPORTS_PATH = '/api/v1/business/reports';
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

function buildClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
  });
  installAuthInterceptors(client);
  return client;
}

let cachedClient: AxiosInstance | null = null;
function client(): AxiosInstance {
  if (!cachedClient) cachedClient = buildClient();
  return cachedClient;
}

export interface ApiFieldError {
  field: string;
  message: string;
}

export class PreInspectionApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly fieldErrors: ApiFieldError[];

  constructor(message: string, status: number, code?: string, fieldErrors: ApiFieldError[] = []) {
    super(message);
    this.name = 'PreInspectionApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

function toFieldErrors(detail: unknown): ApiFieldError[] {
  if (!Array.isArray(detail)) return [];
  return detail
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const loc = (item as { loc?: unknown[] }).loc;
      const msg = (item as { msg?: string }).msg;
      if (!Array.isArray(loc) || typeof msg !== 'string') return null;
      const field = loc.filter((p) => p !== 'body').join('.');
      return { field, message: msg };
    })
    .filter((x): x is ApiFieldError => x !== null);
}

function wrap(err: unknown): PreInspectionApiError {
  if (err instanceof AxiosError && err.response) {
    const status = err.response.status;
    const data = err.response.data as
      | { detail?: unknown; code?: string; message?: string }
      | undefined;
    const message =
      (typeof data?.message === 'string' && data.message) ||
      (typeof data?.detail === 'string' && data.detail) ||
      err.message ||
      'Request failed';
    return new PreInspectionApiError(
      message,
      status,
      data?.code,
      toFieldErrors(data?.detail),
    );
  }
  const message = err instanceof Error ? err.message : 'Network error';
  return new PreInspectionApiError(message, 0);
}

export async function createPreInspection(
  payload: PreInspectionCreateRequest,
): Promise<PreInspectionCreateResponse> {
  try {
    const { data } = await client().post<PreInspectionCreateResponse>(BASE_PATH, payload);
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface ListParams {
  cursor?: string;
  limit?: number;
}

export interface JobSummary {
  id: string;
  tenant_id: string;
  job_number: string;
  job_type: string | null;
  job_type_name: string | null;
  external_ref_number: string | null;
  policy_number: string | null;
  vehicle_id: string | null;
  customer_id: string | null;
  assigned_partner_id: string | null;
  assigned_agent_id: string | null;
  assigned_at: string | null;
  priority: string | null;
  priority_name: string | null;
  sla_due_date: string | null;
  sla_breached: boolean;
  sla_status: string | null;
  status: string | null;
  status_name: string | null;
  sub_status: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobSearchPagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface JobSearchResponse {
  jobs: JobSummary[];
  pagination: JobSearchPagination;
  filters_applied: Record<string, unknown>;
}

export interface JobSearchParams {
  status?: string;
  job_type?: string;
  priority?: string;
  assigned_to?: string;
  assigned_partner_id?: string;
  assigned_agent_id?: string;
  sla_status?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sort?: string;
}

export async function searchJobs(
  params: JobSearchParams = {},
): Promise<JobSearchResponse> {
  const cleaned: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value as string | number;
    }
  }
  try {
    const { data } = await client().get<JobSearchResponse>(JOBS_PATH, {
      params: cleaned,
    });
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function listPreInspections(
  params: ListParams = {},
): Promise<PreInspectionListResponse> {
  try {
    const { data } = await client().get<PreInspectionListResponse>(BASE_PATH, { params });
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function getPreInspection(
  jobId: string,
): Promise<PreInspectionDetailResponse> {
  try {
    const { data } = await client().get<PreInspectionDetailResponse>(`${BASE_PATH}/${jobId}`);
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function updatePreInspection(
  jobId: string,
  payload: PreInspectionUpdateRequest,
): Promise<PreInspectionDetailResponse> {
  try {
    const { data } = await client().patch<PreInspectionDetailResponse>(
      `${BASE_PATH}/${jobId}`,
      payload,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface AllowedTransitionsResponse {
  from_status: string;
  allowed_transitions: string[];
}

export async function getAllowedTransitions(
  fromStatusCode: string,
): Promise<AllowedTransitionsResponse> {
  try {
    const { data } = await client().get<AllowedTransitionsResponse>(
      `${TENANT_STATUS_PATH}/${encodeURIComponent(fromStatusCode)}/transitions`,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface Partner {
  id: string;
  name: string;
  code?: string | null;
}

export async function listPartners(): Promise<Partner[]> {
  try {
    const { data } = await client().get<Partner[]>(PARTNERS_PATH);
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface JobStatusHistoryEntry {
  id: string;
  from_status: string | null;
  to_status: string;
  from_sub_status: string | null;
  to_sub_status: string | null;
  notes: string | null;
  created_at: string;
  created_by: string | null;
}

export interface JobStatusHistoryResponse {
  status_history: JobStatusHistoryEntry[];
}

export async function getJobStatusHistory(
  jobId: string,
): Promise<JobStatusHistoryResponse> {
  try {
    const { data } = await client().get<JobStatusHistoryResponse>(
      `${JOBS_PATH}/${encodeURIComponent(jobId)}/status-history`,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface UpdateJobStatusRequest {
  status: string;
  sub_status?: string | null;
  notes?: string | null;
}

export interface UpdateJobStatusResponse {
  job_id: string;
  status: string;
  sub_status?: string | null;
  updated_at?: string;
  [key: string]: unknown;
}

export async function updateJobStatus(
  jobId: string,
  payload: UpdateJobStatusRequest,
): Promise<UpdateJobStatusResponse> {
  try {
    const { data } = await client().patch<UpdateJobStatusResponse>(
      `${JOBS_PATH}/${encodeURIComponent(jobId)}/status`,
      payload,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface PreInspectionQcResultResponse {
  id: string;
  tenant_id: string;
  job_id: string;
  detection_batch_id: string;
  qc_result_code: string;
  qc_result_label: string;
  recommendation_code: string;
  recommendation_label: string;
  vehicle_condition_code: string | null;
  vehicle_condition_label: string | null;
  confidence_score: number;
  processing_time_ms: number;
  ai_model_version: string | null;
  triggered_by: string;
  triggered_at: string;
  completed_at: string | null;
  raw_response: Record<string, unknown> | null;
  error_details: Record<string, unknown> | null;
}

export async function getPreInspectionQcResult(
  jobId: string,
  options: { includeRaw?: boolean } = {},
): Promise<PreInspectionQcResultResponse> {
  const params: Record<string, boolean> = {};
  if (options.includeRaw) params.include_raw = true;
  try {
    const { data } = await client().get<PreInspectionQcResultResponse>(
      `${AI_QC_PATH}/jobs/${encodeURIComponent(jobId)}/result`,
      { params },
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface InspectionReportResponse {
  id: string;
  tenant_id: string;
  job_id: string;
  qc_result_id: string;
  report_number: string;
  status_code: string;
  status_label: string;
  recommendation_code: string;
  recommendation_label: string;
  vehicle_condition_code: string | null;
  vehicle_condition_label: string | null;
  summary_payload: Record<string, unknown>;
  submitted_by: string | null;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface InspectionReportCreateRequest {
  job_id: string;
  qc_result_id: string;
  recommendation_id: number;
  vehicle_condition_id?: number | null;
  summary_payload?: Record<string, unknown>;
}

export interface InspectionReportEditRequest {
  field_type_id: number;
  old_value?: string | null;
  new_value?: string | null;
  edit_reason?: string | null;
}

export interface InspectionReportEditResponse {
  id: string;
  inspection_report_id: string;
  field_type_id: number;
  old_value: string | null;
  new_value: string | null;
  edit_reason: string | null;
  edited_by: string;
  edited_at: string;
}

export async function createInspectionReport(
  payload: InspectionReportCreateRequest,
): Promise<InspectionReportResponse> {
  try {
    const { data } = await client().post<InspectionReportResponse>(
      REPORTS_PATH,
      payload,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function getInspectionReport(
  reportId: string,
): Promise<InspectionReportResponse> {
  try {
    const { data } = await client().get<InspectionReportResponse>(
      `${REPORTS_PATH}/${encodeURIComponent(reportId)}`,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function addInspectionReportEdit(
  reportId: string,
  edit: InspectionReportEditRequest,
): Promise<InspectionReportEditResponse> {
  try {
    const { data } = await client().post<InspectionReportEditResponse>(
      `${REPORTS_PATH}/${encodeURIComponent(reportId)}/edits`,
      edit,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export async function submitInspectionReport(
  reportId: string,
): Promise<InspectionReportResponse> {
  try {
    const { data } = await client().post<InspectionReportResponse>(
      `${REPORTS_PATH}/${encodeURIComponent(reportId)}/submit`,
    );
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export interface PaidListParams {
  q?: string;
  cursor?: string;
  limit?: number;
}

export async function listPaidPreInspections(
  params: PaidListParams = {},
): Promise<PaidPreInspectionListResponse> {
  const cleaned: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') cleaned[k] = v as string | number;
  }
  try {
    const { data } = await client().get<PaidPreInspectionListResponse>(PAID_PATH, {
      params: cleaned,
    });
    return data;
  } catch (err) {
    throw wrap(err);
  }
}

export function getPaidReportDownloadUrl(jobId: string): string {
  return `${PAID_PATH}/${encodeURIComponent(jobId)}/report`;
}
